// Read-only status recovery for a CV upload task after its SSE stream drops.
// Only GETs the existing /upload-progress/{task_id} route: never re-uploads.

export const POLL_INTERVAL_MS = 5000;
export const POLL_MAX_DURATION_MS = 5 * 60 * 1000;
export const CHECK_TIMEOUT_MS = 4000;

// Backend SSE emits this when the task row vanished mid-stream: not a real failure.
export const isTaskGoneError = (data) => data?.status === 'error' && data?.error === 'Task not found';

const readFirstFrame = async (body) => {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
        for (;;) {
            const { value, done } = await reader.read();
            if (value) buffer += decoder.decode(value, { stream: true });
            const end = buffer.indexOf('\n\n');
            if (end !== -1 || done) {
                const frame = end !== -1 ? buffer.slice(0, end) : buffer;
                const payload = frame.split('\n').filter(l => l.startsWith('data:')).map(l => l.slice(5).trim()).join('\n');
                return payload ? JSON.parse(payload) : null;
            }
        }
    } finally {
        reader.cancel().catch(() => {});
    }
};

// One status check. Resolves to { kind: 'status'|'not_found'|'unauthorized'|'transient'|'aborted', data? }.
export const fetchUploadTaskStatus = async (url, { timeoutMs = CHECK_TIMEOUT_MS, signal } = {}) => {
    const controller = new AbortController();
    const onAbort = () => controller.abort();
    if (signal) {
        if (signal.aborted) return { kind: 'aborted' };
        signal.addEventListener('abort', onAbort);
    }
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await fetch(url, { credentials: 'include', signal: controller.signal, headers: { Accept: 'text/event-stream' } });
        if (res.status === 404) return { kind: 'not_found' };
        if (res.status === 401) return { kind: 'unauthorized' };
        if (!res.ok || !res.body) return { kind: 'transient' };
        const data = await readFirstFrame(res.body);
        if (!data || typeof data.status !== 'string') return { kind: 'transient' };
        if (isTaskGoneError(data)) return { kind: 'not_found' };
        return { kind: 'status', data };
    } catch {
        return signal?.aborted ? { kind: 'aborted' } : { kind: 'transient' };
    } finally {
        clearTimeout(timer);
        controller.abort();
        if (signal) signal.removeEventListener('abort', onAbort);
    }
};

const abortableSleep = (ms, signal) => new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const done = () => { clearTimeout(timer); signal?.removeEventListener('abort', done); resolve(); };
    const timer = setTimeout(done, ms);
    signal?.addEventListener('abort', done);
});

// Sequential polling (never two checks in flight). Resolves to
// { outcome: 'complete', data } | { outcome: 'error', message } |
// { outcome: 'unavailable', reason: 'not_found'|'timeout' } | { outcome: 'unauthorized' } | { outcome: 'aborted' }.
export const pollUploadTask = async ({
    url,
    signal,
    onProgress,
    intervalMs = POLL_INTERVAL_MS,
    maxDurationMs = POLL_MAX_DURATION_MS,
    checkTimeoutMs = CHECK_TIMEOUT_MS,
    fetchStatus = fetchUploadTaskStatus,
    sleep = abortableSleep,
    now = Date.now,
}) => {
    const deadline = now() + maxDurationMs;
    for (let first = true; ; first = false) {
        if (!first) {
            const remaining = deadline - now();
            if (remaining <= 0) return { outcome: 'unavailable', reason: 'timeout' };
            await sleep(Math.min(intervalMs, remaining), signal);
        }
        if (signal?.aborted) return { outcome: 'aborted' };
        if (now() >= deadline) return { outcome: 'unavailable', reason: 'timeout' };
        const res = await fetchStatus(url, { timeoutMs: checkTimeoutMs, signal });
        if (signal?.aborted || res.kind === 'aborted') return { outcome: 'aborted' };
        if (res.kind === 'not_found') return { outcome: 'unavailable', reason: 'not_found' };
        if (res.kind === 'unauthorized') return { outcome: 'unauthorized' };
        if (res.kind === 'status') {
            const { data } = res;
            if (data.status === 'complete') return { outcome: 'complete', data };
            if (data.status === 'error') return { outcome: 'error', message: data.error || 'Erreur' };
            if (onProgress) onProgress(data);
        }
    }
};
